import React, { useState, useEffect } from 'react';
import { KLOOK_AID } from '../constants';
import supabase from '../supabase';

// Klook 聯盟 Hotel Deals：無優惠碼，經酒店連結訂房自動享折扣。
// 資料由 scripts/import_klook_hotel_deals.py 從 Klook 給的 Excel 匯入 klook_hotel_deals 表。

const CACHE_KEY = 'ec_klook_hotel_deals_v1';
const CACHE_TTL = 60 * 60 * 1000; // 1 hour

// 行程目的地字串因介面語言而異（上海／Shanghai／상하이），逐一列出別名比對
const CITY_ALIASES = {
  Shanghai:     ['上海', 'shanghai', '상하이'],
  Beijing:      ['北京', 'beijing', 'peking', '베이징'],
  Shenzhen:     ['深圳', 'shenzhen', '深セン', '선전'],
  Zhuhai:       ['珠海', 'zhuhai', '주하이'],
  Taipei:       ['台北', '臺北', 'taipei', '타이베이'],
  'New Taipei': ['新北', 'new taipei', '新北市', '신베이'],
  Tainan:       ['台南', '臺南', 'tainan', '타이난'],
  Taichung:     ['台中', '臺中', 'taichung', '타이중'],
  Yilan:        ['宜蘭', '宜兰', 'yilan', '이란'],
  Taitung:      ['台東', '臺東', '台东', 'taitung', '타이둥'],
};
const CITY_ZH = {
  Shanghai:'上海', Beijing:'北京', Shenzhen:'深圳', Zhuhai:'珠海', Taipei:'台北',
  'New Taipei':'新北', Tainan:'台南', Taichung:'台中', Yilan:'宜蘭', Taitung:'台東',
};

const isZhLang = (lang) => !lang || lang.startsWith('zh');
const dealLink = (hotelId) => /^\d+$/.test(hotelId) ? `https://www.klook.com/hotels/detail/${hotelId}?aid=${KLOOK_AID}` : '#';

const cityKeyOf = (raw) => {
  const s = String(raw || '').trim().toLowerCase();
  if (!s) return null;
  return Object.keys(CITY_ALIASES).find(k => CITY_ALIASES[k].includes(s)) || null;
};

// 讀取有效優惠：過濾已過期，同一酒店有多個優惠時只留折扣最高的一個，按折扣由高到低排
export const useKlookHotelDeals = () => {
  const [deals, setDeals] = useState([]);
  useEffect(() => {
    const cached = (() => { try { const d = JSON.parse(sessionStorage.getItem(CACHE_KEY)); return d?.ts && Date.now()-d.ts < CACHE_TTL ? d.data : null; } catch { return null; } })();
    if (cached) { setDeals(cached); return; }
    supabase.from('klook_hotel_deals').select('hotel_id,hotel_name,city,offer_en,offer_zh,max_pct,stay_end').eq('is_active', true)
      .then(({ data, error }) => {
        if (error) { console.error('[KlookHotelDeals] fetch error:', error.message, error.code); return; }
        if (data?.length) { setDeals(data); try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data })); } catch {} }
      });
  }, []);
  // 按香港時間判斷日期（優惠酒店在中國/台灣，同為 UTC+8），避免 UTC 令過期優惠多顯示 8 小時
  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Hong_Kong' });
  const best = new Map();
  for (const d of deals) {
    if (d.stay_end && d.stay_end < today) continue;
    const prev = best.get(d.hotel_id);
    if (!prev || (d.max_pct ?? -1) > (prev.max_pct ?? -1)) best.set(d.hotel_id, d);
  }
  return [...best.values()].sort((a, b) => (b.max_pct ?? -1) - (a.max_pct ?? -1));
};

const DealRow = ({ d, lang, showCity }) => {
  const zh = isZhLang(lang);
  const until = d.stay_end ? `${zh ? '優惠至' : 'Until'} ${d.stay_end}` : (zh ? '未列明截止日' : 'No end date listed');
  return (
    <a href={dealLink(d.hotel_id)} target="_blank" rel="sponsored noopener noreferrer"
      style={{ textDecoration:'none', color:'#0f172a', display:'flex', alignItems:'center', gap:10, padding:'11px 0', borderBottom:'1px solid #f1f5f9', minHeight:44 }}>
      <span style={{ flex:1, minWidth:0, display:'flex', flexDirection:'column', gap:3 }}>
        <span style={{ fontSize:13, fontWeight:800, lineHeight:1.35 }}>{d.hotel_name}</span>
        <span style={{ fontSize:12, fontWeight:800, color:'#c2410c', lineHeight:1.4 }}>
          {showCity && <span style={{ color:'#475569', fontWeight:700 }}>{zh ? (CITY_ZH[d.city] || d.city) : d.city} · </span>}
          {zh ? d.offer_zh : d.offer_en}
        </span>
        <span style={{ fontSize:10.5, color:'#64748b' }}>{until}</span>
      </span>
      <span style={{ flexShrink:0, fontSize:11.5, fontWeight:900, color:'#fff', background:'#ff5a00', borderRadius:10, padding:'0 12px', height:40, display:'flex', alignItems:'center' }}>
        {zh ? '去 Klook' : 'Go to Klook'}
      </span>
    </a>
  );
};

const SourceNote = ({ lang }) => (
  <p style={{ margin:'10px 0 0', fontSize:10.5, color:'#64748b', lineHeight:1.5, textAlign:'center' }}>
    {isZhLang(lang)
      ? '優惠資料來自 Klook 聯盟 · 折扣在 Klook 酒店頁自動套用，以 Klook 頁面為準'
      : 'Deals provided by Klook · discount applies automatically on the Klook hotel page; Klook\'s page is authoritative'}
  </p>
);

// 行程頁「預訂你的行程」：目的地有優惠才出現
export const KlookHotelDealsCard = ({ city, lang }) => {
  const all = useKlookHotelDeals();
  const [open, setOpen] = useState(false);
  const key = cityKeyOf(city);
  const list = key ? all.filter(d => d.city === key) : [];
  if (!list.length) return null;
  const zh = isZhLang(lang);
  const shown = open ? list : list.slice(0, 4);
  return (
    <div style={{ borderRadius:16, border:'1.5px solid #fed7aa', overflow:'hidden', background:'#fff' }}>
      <div style={{ background:'#ff5a00', padding:'10px 16px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:8 }}>
        <p style={{ margin:0, fontSize:13, fontWeight:900, color:'#fff' }}>
          {zh ? `${CITY_ZH[key] || city} Klook 酒店優惠` : `Klook hotel deals in ${key}`}
        </p>
        <span style={{ fontSize:10.5, fontWeight:800, color:'#fff', background:'rgba(0,0,0,0.2)', borderRadius:999, padding:'3px 8px' }}>
          {list.length} {zh ? '間' : list.length > 1 ? 'hotels' : 'hotel'}
        </span>
      </div>
      <div style={{ padding:'2px 14px 12px', display:'flex', flexDirection:'column' }}>
        {shown.map(d => <DealRow key={d.hotel_id} d={d} lang={lang}/>)}
        {list.length > 4 && (
          <button onClick={() => setOpen(o => !o)}
            style={{ marginTop:8, height:44, borderRadius:10, border:'1.5px solid #fed7aa', background:'#fff7ed', color:'#c2410c', fontSize:12, fontWeight:900, cursor:'pointer' }}>
            {open ? (zh ? '收起' : 'Show less') : (zh ? `查看全部 ${list.length} 間優惠酒店` : `See all ${list.length} hotels`)}
          </button>
        )}
        <SourceNote lang={lang}/>
      </div>
    </div>
  );
};

// 優惠頁：按城市篩選的完整列表
export const KlookHotelDealsSection = ({ lang }) => {
  const all = useKlookHotelDeals();
  const [cityFilter, setCityFilter] = useState('all');
  if (!all.length) return null;
  const zh = isZhLang(lang);
  const counts = all.reduce((m, d) => { m[d.city] = (m[d.city] || 0) + 1; return m; }, {});
  const cities = Object.keys(counts).sort((a, b) => counts[b] - counts[a]);
  const cur = counts[cityFilter] ? cityFilter : 'all';
  const list = cur === 'all' ? all : all.filter(d => d.city === cur);
  return (
    <div style={{ background:'#fff', border:'1.5px solid #e2e8f0', borderRadius:14, overflow:'hidden', marginTop:10 }}>
      <div style={{ padding:'16px 16px 10px' }}>
        <p style={{ margin:0, fontSize:14, fontWeight:900, color:'#0f172a' }}>🏨 {zh ? 'Klook 酒店優惠' : 'Klook Hotel Deals'}</p>
        <p style={{ margin:'4px 0 0', fontSize:12, color:'#64748b', lineHeight:1.5 }}>
          {zh ? '不用優惠碼，經連結訂房自動享有折扣。' : 'No code needed — the discount applies when you book through the link.'}
        </p>
        <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginTop:10 }}>
          {['all', ...cities].map(c => {
            const on = cur === c;
            return (
              <button key={c} onClick={() => setCityFilter(c)}
                style={{ padding:'5px 12px', borderRadius:99, border:`1.5px solid ${on?'#0f172a':'#e2e8f0'}`, background:on?'#0f172a':'#fff', color:on?'#fff':'#64748b', fontSize:12, fontWeight:700, cursor:'pointer' }}>
                {c === 'all' ? (zh ? '全部' : 'All') : (zh ? (CITY_ZH[c] || c) : c)} <span style={{ opacity:0.6, fontWeight:600 }}>{c === 'all' ? all.length : counts[c]}</span>
              </button>
            );
          })}
        </div>
      </div>
      <div style={{ padding:'0 16px 12px', display:'flex', flexDirection:'column' }}>
        {list.map(d => <DealRow key={d.hotel_id} d={d} lang={lang} showCity={cur === 'all'}/>)}
        <SourceNote lang={lang}/>
      </div>
    </div>
  );
};
